import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowUpRight, BriefcaseIcon, ShieldCheckIcon } from "@/components/icons";
import { jobTitle } from "@/lib/format";
import type { JobDetail } from "@/lib/types";
import { getJob } from "@/src/api";
import { useAuth } from "@/src/auth-context";
import { useDocumentMeta } from "@/src/use-document-meta";

type AccountSummary = {
  click_count: number;
  remaining: number;
  recent: { job_id: string; created_at: number }[];
};

type Activity = AccountSummary["recent"][number] & { job: JobDetail | null };

export function DashboardPage() {
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState<AccountSummary | null>();
  const [activity, setActivity] = useState<Activity[]>([]);

  useDocumentMeta({
    title: "Dashboard | Chuno",
    description: "Manage your Chuno job-search activity.",
    robots: "noindex,nofollow",
    canonical: "https://www.chuno.work/dashboard",
  });

  useEffect(() => {
    if (!user) return;
    let active = true;
    void user.getIdToken().then((token) => fetch("/api/account/summary", {
      headers: { Authorization: `Bearer ${token}` },
    })).then(async (response) => {
      if (!response.ok) throw new Error("summary unavailable");
      return response.json() as Promise<AccountSummary>;
    }).then(async (result) => {
      if (!active) return;
      setSummary(result);
      const rows = await Promise.all(result.recent.map(async (item) => ({ ...item, job: await getJob(item.job_id) })));
      if (active) setActivity(rows);
    }).catch(() => {
      if (active) setSummary(null);
    });
    return () => { active = false; };
  }, [user]);

  if (loading) return <main id="main" className="page-loading"><div><span />Loading your dashboard…</div></main>;
  if (!user) return <Navigate to="/login?return_to=%2Fdashboard" replace />;

  const name = user.displayName || user.email?.split("@")[0] || "there";

  return (
    <main id="main" className="account-page">
      <div className="shell account-shell">
        <header className="account-hero">
          <div><p className="eyebrow"><span /> Dashboard</p><h1>Welcome back, {name}.</h1><p>Keep track of your Chuno activity and account.</p></div>
          <Link className="button button--dark" to="/jobs">Find jobs <ArrowUpRight /></Link>
        </header>

        <section className="account-stats" aria-label="Account overview">
          <article><BriefcaseIcon /><p>Apply clicks used</p><strong>{summary?.click_count ?? "—"}</strong><span>of 50</span></article>
          <article><ShieldCheckIcon /><p>Apply clicks remaining</p><strong>{summary?.remaining ?? "—"}</strong><span>on this account</span></article>
          <article><span className="account-avatar">{name.slice(0, 1).toUpperCase()}</span><p>Account</p><strong className="account-stat-email">{user.email}</strong><Link to="/settings">Manage settings</Link></article>
        </section>

        <section className="account-activity">
          <div className="section-heading"><div><p className="section-kicker">Recent activity</p><h2>Your apply activity.</h2></div></div>
          {summary === undefined ? <div className="account-loading">Loading activity…</div> : null}
          {summary === null ? <div className="account-empty"><h3>Activity is unavailable.</h3><p>Please refresh and try again.</p></div> : null}
          {summary && !activity.length ? <div className="account-empty"><h3>No activity yet.</h3><p>Explore current roles and use Apply when you find a match.</p><Link to="/jobs">Browse jobs <ArrowUpRight /></Link></div> : null}
          {activity.length ? <div className="activity-list">{activity.map((item) => <article key={`${item.job_id}-${item.created_at}`}><div><p>{item.job?.company || "Employer"}</p><h3>{item.job ? jobTitle(item.job) : "Job opportunity"}</h3><span>{activityDate(item.created_at)}</span></div><Link to={`/jobs/${item.job_id}`} reloadDocument>View role <ArrowUpRight /></Link></article>)}</div> : null}
        </section>
      </div>
    </main>
  );
}

function activityDate(value: number) {
  const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) return "Application opened";
  const today = new Date();
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const daysAgo = Math.round((currentDay - day) / 86_400_000);
  if (daysAgo === 0) return "Opened today";
  if (daysAgo === 1) return "Opened yesterday";
  return `Opened ${date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric" })}`;
}
